import { WORKSPACE_HEADER } from '@coworker/contracts';
import type { z } from 'zod';

import { ApiError, decodeApiError } from './api-error.js';

/**
 * The one way this app talks to the control API - WEB-004, ADR-031.
 *
 * Every response is parsed with the same zod schema the API validates against,
 * because `packages/contracts` is isomorphic and the alternative is a cast that
 * agrees with whatever the caller assumed. A shape change on the server then
 * fails here, loudly, instead of surfacing three components later as
 * `undefined`.
 *
 * The token is read through a function rather than captured: it is held in
 * memory, replaced when a login completes and dropped when one expires, so a
 * client built once at start-up would otherwise keep sending the first token it
 * ever saw - or keep sending one after the session was dropped.
 */
/**
 * How long any one request may take before this client gives up on it.
 *
 * **The reason, because a bare number here would be a guess.** The API Lambda
 * is killed at ten seconds (`infra/aws-cdk/lib/api-stack.ts`), and API
 * Gateway's own ceiling is thirty, so a response that has not arrived after the
 * server stopped working on it is lost rather than late.
 *
 * Twelve and not eleven because the ten seconds bounds the *handler*: a cold
 * start's init, DNS and TLS on a fresh connection all sit outside it and are
 * wall clock the browser waits for too. So the margin is small, not generous,
 * and the number is a floor with a little room rather than a tuned value -
 * anything from about eleven to twenty-nine behaves the same. The measured cold
 * and warm figures behind it are in `.claude/rules/seams.md`.
 *
 * COW-488 is why this exists at all. Nothing in this app passed an
 * `AbortSignal`, so a request that never settled left its query `pending`
 * forever: TanStack retries a rejection and there was none. The measured
 * symptom was a spinner reading "Loading your workspace…" 107 seconds in, with
 * no error and no way out but a manual reload.
 */
export const REQUEST_TIMEOUT_MS = 12_000;

export interface ApiClientOptions {
  readonly baseUrl: string;
  /** `null` before sign-in, and between an expiry and the refresh that replaces it. */
  readonly accessToken: () => string | null;
  /** Injected so a test drives real `Response` objects rather than a mocked module. */
  readonly fetchImpl?: typeof fetch;
}

export interface RequestOptions<T> {
  readonly method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  readonly body?: unknown;
  /** Sent as `x-workspace-id`; ADR-030 keeps the workspace out of the path. */
  readonly workspaceId?: string;
  /** The contract this response must satisfy. */
  readonly schema: z.ZodType<T>;
  /**
   * Aborts this request. Composed with the client's own timeout rather than
   * replacing it, so passing one cannot accidentally restore the unbounded wait.
   */
  readonly signal?: AbortSignal;
  /** Overrides `REQUEST_TIMEOUT_MS`. Only a test needs this. */
  readonly timeoutMs?: number;
  /**
   * Extra request headers.
   *
   * Added for COW-483's session routes, which require `x-coworker-session` -
   * a header a cross-site form cannot set, which is what forces a preflight
   * and makes it the CSRF defence. Nothing else uses this.
   */
  readonly headers?: Readonly<Record<string, string>>;
  /**
   * `'include'` on the session routes, and nowhere else.
   *
   * Without it the browser ignores `Set-Cookie` on a cross-origin response
   * entirely - and the request still succeeds, so the failure is silent and
   * leaves no session behind. Deliberately opt-in: sending the cookie on every
   * call would attach a credential to requests that have no use for one.
   */
  readonly credentials?: RequestCredentials;
}

export class ApiClient {
  readonly #baseUrl: string;
  readonly #accessToken: () => string | null;
  readonly #fetch: typeof fetch;

  constructor(options: ApiClientOptions) {
    this.#baseUrl = options.baseUrl;
    this.#accessToken = options.accessToken;
    this.#fetch = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
  }

  async request<T>(path: string, options: RequestOptions<T>): Promise<T> {
    const headers = new Headers({ accept: 'application/json' });

    const token = this.#accessToken();
    // Deliberately still sent without one: the gateway's 401 is the answer, and
    // short-circuiting here would invent a different error for the same state.
    if (token !== null) headers.set('authorization', `Bearer ${token}`);
    if (options.workspaceId !== undefined) headers.set(WORKSPACE_HEADER, options.workspaceId);
    if (options.body !== undefined) headers.set('content-type', 'application/json');
    for (const [name, value] of Object.entries(options.headers ?? {})) headers.set(name, value);

    const deadline = this.#deadline(options);

    try {
      return await this.#exchange(path, options, headers, deadline.signal);
    } catch (error) {
      // Rethrown as the envelope every other failure arrives in, so the error
      // card renders a sentence rather than "This operation was aborted". 504
      // and not 500 because it is true, and because `retryableStatus` reads the
      // status: a request that ran out of time is worth one more attempt.
      if (deadline.timedOut()) {
        throw new ApiError({
          status: 504,
          code: 'TIMEOUT',
          message: `the API did not answer ${path} within ${deadline.seconds} seconds`,
        });
      }
      throw error;
    } finally {
      deadline.dispose();
    }
  }

  /**
   * One request and its response body, under a signal it does not own.
   *
   * Split out so the deadline in `request` covers **both** phases. `fetch`
   * resolves when the response *headers* arrive; the body is read afterwards,
   * here. An earlier draft armed and cleared the timer around the `fetch` alone,
   * which left the exact COW-488 hang alive one phase later - measured, with a
   * body stream that never closes: a 20 ms deadline was still pending 400 ms in,
   * because the timer had already been cleared and nothing was left to fire.
   */
  async #exchange<T>(
    path: string,
    options: RequestOptions<T>,
    headers: Headers,
    signal: AbortSignal,
  ): Promise<T> {
    const response = await this.#fetch(`${this.#baseUrl}${path}`, {
      method: options.method ?? 'GET',
      headers,
      ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
      ...(options.credentials === undefined ? {} : { credentials: options.credentials }),
      signal,
    });

    if (!response.ok) {
      const decoded = await decodeApiError(response);
      // `decodeApiError` is total on purpose - "a decoder that can fail turns
      // one error into two" - so it reports a body that stopped arriving as an
      // unreadable one, exactly as the success path below used to. The
      // difference matters here: without this the client fabricates a 500 the
      // server never sent, and `retryableStatus` sends it round three times.
      if (signal.aborted) throw signal.reason;
      throw decoded;
    }

    // The `catch` is for a body that is empty or not JSON, which is a real
    // answer this client has to survive. A body that *stopped arriving* is not
    // that, and swallowing it would report a torn response as an empty one - a
    // schema permissive enough to accept `undefined` would then resolve, and
    // the deadline above would never be consulted. Measured with a stream that
    // never closes: the request resolved with `undefined` instead of timing out.
    const payload: unknown = await response.json().catch((error: unknown) => {
      if (signal.aborted) throw error;
      return undefined;
    });
    const parsed = options.schema.safeParse(payload);
    if (!parsed.success) {
      throw new ApiError({
        status: response.status,
        code: 'UNREADABLE',
        message: `${path} answered a shape this client does not accept: ${parsed.error.issues
          .map((issue) => `${issue.path.join('.') || '(root)'} ${issue.message}`)
          .join('; ')}`,
      });
    }

    return parsed.data;
  }

  /**
   * A deadline for one whole exchange, composed with the caller's own signal.
   *
   * `AbortSignal.any` would say this in one line. It is deliberately not used:
   * this is the only thing standing between a stalled response and the
   * unbounded spinner COW-488 is about, and wiring two listeners by hand cannot
   * half-work on a runtime that lacks it. That is a small, cheap insurance
   * premium rather than a claim about any particular browser - this repository
   * declares no `browserslist` and no build target, so it has no support matrix
   * to appeal to.
   */
  #deadline(options: RequestOptions<unknown>): {
    readonly signal: AbortSignal;
    readonly seconds: number;
    timedOut: () => boolean;
    dispose: () => void;
  } {
    const ms = options.timeoutMs ?? REQUEST_TIMEOUT_MS;
    const controller = new AbortController();
    const caller = options.signal;
    // The caller's reason is carried, not passed through raw. Forwarding it
    // verbatim was worse than losing it: an abort reason of `new Error('user
    // navigated away')` is named `Error`, so the retry policy could not tell it
    // from a network failure and retried the cancel three times. Measured.
    // Wrapping keeps the message and the original as `cause`, and gives
    // everything downstream one name to switch on.
    const abortFromCaller = () => {
      const reason: unknown = caller?.reason;
      const carried =
        reason instanceof Error
          ? new Error(reason.message, { cause: reason })
          : new Error('the caller aborted this request');
      carried.name = 'AbortError';
      controller.abort(carried);
    };

    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, ms);

    if (caller?.aborted === true) abortFromCaller();
    else caller?.addEventListener('abort', abortFromCaller, { once: true });

    return {
      signal: controller.signal,
      seconds: ms / 1000,
      timedOut: () => timedOut,
      dispose: () => {
        clearTimeout(timer);
        caller?.removeEventListener('abort', abortFromCaller);
      },
    };
  }
}
